import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';

import { crmApi, errMsg } from '@crm/api';
import { canManageCrmAccess, hasCrmPermission } from '@crm/permissions';
import {
  managersThunks,
  managersSelectors,
  managersActions,
} from '@crm/features/managers/managersSlice';
import { UiButton, UiCheckbox, UiInput, UiNativeSelect, UiSelect, UiTextarea, UiTimePicker } from '@shared/common/ui';
import { monthEndDateOnly, todayDateOnly } from '@shared/helpers/dates';

function roleLabel(manager) {
  if (manager.isOwner || manager.role === 99) return 'Директор';
  const savedRoles = Array.isArray(manager.roles) ? manager.roles.map(Number) : [];
  const roles = [];
  if (savedRoles.length) {
    if (savedRoles.includes(2)) roles.push('Администратор');
    if (savedRoles.includes(3)) roles.push('Педагог');
    return roles.length ? roles.join(', ') : 'Администратор';
  }
  if (manager.role === 2 || (manager.role === 1 && !manager.hasCoach)) {
    roles.push('Администратор');
  }
  if (manager.role === 3 || manager.hasCoach) roles.push('Педагог');
  return roles.length ? roles.join(', ') : 'Администратор';
}

function isAdminRole(manager) {
  const savedRoles = Array.isArray(manager?.roles) ? manager.roles.map(Number) : [];
  if (savedRoles.length) return Boolean(manager?.isOwner || manager?.role === 99 || savedRoles.includes(2));
  return Boolean(
    manager?.isOwner ||
    manager?.role === 99 ||
    savedRoles.includes(2) ||
    manager?.role === 2 ||
    (manager?.role === 1 && !manager?.hasCoach),
  );
}

function isCoachRole(manager) {
  const savedRoles = Array.isArray(manager?.roles) ? manager.roles.map(Number) : [];
  if (savedRoles.length) return savedRoles.includes(3);
  return Boolean(manager?.role === 3 || manager?.hasCoach);
}

function otherBranchNames(manager, currentBranchId, currentBranchName) {
  if (Array.isArray(manager?.branchesInfo) && manager.branchesInfo.length) {
    return manager.branchesInfo
      .filter((branch) => String(branch?.id) !== String(currentBranchId))
      .map((branch) => branch?.name)
      .filter(Boolean);
  }

  if (Array.isArray(manager?.branchNames) && manager.branchNames.length) {
    return manager.branchNames.filter((name) => name && name !== currentBranchName);
  }

  return [];
}

const STATUS_LABELS = { 0: 'Заблокирован', 1: 'Активен', 2: 'Активен' };
const ADMIN_PERMISSION_PRESETS = [
  { key: 'manager', label: 'Управляющая' },
  { key: 'senior_admin', label: 'Старший администратор' },
  { key: 'admin', label: 'Администратор' },
];

const DEFAULT_ADMIN_PERMISSION_PRESET = 'admin';

function adminAccessLevelLabel(presetKey) {
  return ADMIN_PERMISSION_PRESETS.find((preset) => preset.key === presetKey)?.label ?? 'Администратор';
}
const formStatus = (status) => (Number(status) === 0 ? 0 : 1);
const adminSalaryType = (salaryType) => (Number(salaryType) === 3 ? '' : salaryType ?? '');
const WEEK_DAYS = [
  { value: '1', label: 'Понедельник' },
  { value: '2', label: 'Вторник' },
  { value: '3', label: 'Среда' },
  { value: '4', label: 'Четверг' },
  { value: '5', label: 'Пятница' },
  { value: '6', label: 'Суббота' },
  { value: '7', label: 'Воскресенье' },
];

function todayString() {
  return todayDateOnly();
}

function monthEndString(date = new Date()) {
  return monthEndDateOnly(date);
}

function emptyScheduleForm() {
  return {
    type: 'regular',
    once: { date: todayString(), timeStart: '10:00', timeEnd: '18:00' },
    regular: {},
  };
}

function parseSchedule(raw) {
  if (!raw) return emptyScheduleForm();
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.type === 'once' || parsed?.type === 'regular') {
      return {
        type: parsed.type,
        once: {
          date: parsed.once?.date || todayString(),
          timeStart: parsed.once?.timeStart || '10:00',
          timeEnd: parsed.once?.timeEnd || '18:00',
        },
        regular: parsed.regular && typeof parsed.regular === 'object' ? parsed.regular : {},
      };
    }
  } catch {
    return emptyScheduleForm();
  }
  return emptyScheduleForm();
}

function serializeSchedule(form) {
  if (form.type === 'once') {
    return JSON.stringify({
      type: 'once',
      once: {
        date: form.once.date,
        timeStart: form.once.timeStart,
        timeEnd: form.once.timeEnd,
      },
      regular: {},
    });
  }
  const regular = Object.fromEntries(
    Object.entries(form.regular ?? {}).filter(([, value]) => value?.timeStart && value?.timeEnd),
  );
  return JSON.stringify({ type: 'regular', once: form.once, regular });
}

export function StaffsPage() {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const crmBranches = useSelector((s) => s.crmAuth.branches ?? []);

  const list = useSelector(managersSelectors.selectList(branchId));
  const listStatus = useSelector(managersSelectors.selectListStatus(branchId));
  const saveStatus = useSelector(managersSelectors.selectSaveStatus(branchId));
  const saveError = useSelector(managersSelectors.selectSaveError(branchId));

  const [createOpen, setCreateOpen] = useState(false);
  const [filters, setFilters] = useState({
    fio: '',
    email: '',
    employeeType: '',
    status: '',
  });
  const currentBranchName = crmBranches.find((branch) => String(branch.id) === String(branchId))?.name ?? null;

  const listParams = {
    fio: filters.fio || undefined,
    email: filters.email || undefined,
    employeeType: filters.employeeType || undefined,
    status: filters.status || undefined,
    limit: 500,
  };

  const reloadList = () => {
    if (branchId) dispatch(managersThunks.list({ branchId, params: listParams }));
  };

  useEffect(() => {
    reloadList();
  }, [dispatch, branchId, filters]);

  return (
    <section className="staffs-page">
      <header className="page-header">
        <h1>Сотрудники</h1>
        <div className="page-header__spacer" />
        <UiButton
          type="button"
          onClick={() => {
            dispatch(managersActions.resetSave(branchId));
            setCreateOpen(true);
          }}
        >
          Новый сотрудник
        </UiButton>
      </header>

      <div className="form-card form-card--wide" style={{ marginTop: 16 }}>
        <div className="form-grid">
          <UiInput
            label="Ф.И.О."
            value={filters.fio}
            onChange={(e) => setFilters((prev) => ({ ...prev, fio: e.target.value }))}
            placeholder="Введите Ф.И.О."
          />
          {/* <UiInput
            label="Email"
            value={filters.email}
            onChange={(e) => setFilters((prev) => ({ ...prev, email: e.target.value }))}
            placeholder="Введите email"
          /> */}
          <label className="ui-field">
            <span className="ui-field__label">Тип сотрудника</span>
            <UiNativeSelect
              value={filters.employeeType}
              onChange={(e) => setFilters((prev) => ({ ...prev, employeeType: e.target.value }))}
            >
              <option value="">Все</option>
              <option value="owner">Директор</option>
              <option value="admin">Администратор</option>
              <option value="coach">Педагог</option>
            </UiNativeSelect>
          </label>
          <label className="ui-field">
            <span className="ui-field__label">Статус</span>
            <UiNativeSelect
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
            >
              <option value="">Все</option>
              <option value="1">Активен</option>
              <option value="0">Заблокирован</option>
            </UiNativeSelect>
          </label>
        </div>
        <div className="form-actions left">
          <UiButton
            variant="ghost"
            type="button"
            onClick={() => setFilters({ fio: '', email: '', employeeType: '', status: '' })}
          >
            Очистить
          </UiButton>
        </div>
      </div>

      <StaffCreateModal
        open={createOpen}
        branchId={branchId}
        saveStatus={saveStatus}
        saveError={saveError}
        onClose={() => {
          setCreateOpen(false);
          dispatch(managersActions.resetSave(branchId));
        }}
        onCreated={(createdManager) => {
          setCreateOpen(false);
          dispatch(managersActions.resetSave(branchId));
          if (createdManager?.id) navigate(String(createdManager.id));
        }}
      />

      <div className="form-card form-card--wide staffs-page__table-card" style={{ marginTop: 16 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Ф.И.О.</th>
              {/* <th>Email</th> */}
              <th>Тип</th>
              <th>Другие филиалы</th>
              <th>Статус</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.map((m) => (
              <tr key={m.id}>
                <td>{m.name ?? '—'}</td>
                {/* <td>{m.login}</td> */}
                <td>{roleLabel(m)}</td>
                <td>{otherBranchNames(m, branchId, currentBranchName).join(', ') || '—'}</td>
                <td>{STATUS_LABELS[m.status] ?? m.status}</td>
                <td style={{ textAlign: 'right' }}>
                  <UiButton variant="ghost" size="sm"

                    type="button"
                    onClick={() => navigate(m.id)}
                  >
                    Открыть
                  </UiButton>
                  {!m.isOwner && (
                    <UiButton variant="danger" size="sm"

                      type="button"
                      onClick={() => {
                        if (window.confirm('Убрать сотрудника из филиала?')) {
                          dispatch(managersThunks.remove({ branchId, id: m.id }));
                        }
                      }}
                    >
                      Удалить
                    </UiButton>
                  )}
                </td>
              </tr>
            ))}
            {!list.length && listStatus !== 'loading' && (
              <tr>
                <td colSpan={5} style={{ color: 'var(--color-muted)', textAlign: 'center' }}>
                  Пока нет сотрудников
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function formatTelegramLinkedAt(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function StaffNotificationsSection({
  branchId,
  managerId,
  manager,
  canManage,
  onUpdated,
}) {
  const [telegramLink, setTelegramLink] = useState(null);
  const [telegramLinkLoading, setTelegramLinkLoading] = useState(false);
  const [telegramLinkError, setTelegramLinkError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [copyLinkState, setCopyLinkState] = useState(null);

  const telegram = manager?.notifications?.telegram;
  const subscriptions = manager?.notifications?.subscriptions ?? [];
  const linked = Boolean(telegram?.linked);
  const currentBranchName = subscriptions[0]?.branchName;

  useEffect(() => {
    if (!telegramLink) return undefined;
    const poll = setInterval(async () => {
      try {
        const { data } = await crmApi(branchId).get(`/managers/${managerId}`);
        onUpdated(data.manager);
        if (data.manager?.notifications?.telegram?.linked) {
          setTelegramLink(null);
        }
      } catch {
        /* polling */
      }
    }, 5000);
    const expire = setTimeout(() => setTelegramLink(null), telegramLink.expiresIn * 1000);
    return () => {
      clearInterval(poll);
      clearTimeout(expire);
    };
  }, [telegramLink, branchId, managerId, onUpdated]);

  const createTelegramLink = async () => {
    setTelegramLinkLoading(true);
    setTelegramLinkError(null);
    setCopyLinkState(null);
    try {
      const { data } = await crmApi(branchId).post(`/managers/${managerId}/telegram-link`);
      setTelegramLink(data);
    } catch (e) {
      setTelegramLinkError(errMsg(e, 'Не удалось создать код привязки'));
    } finally {
      setTelegramLinkLoading(false);
    }
  };

  const unlinkTelegram = async () => {
    if (!window.confirm('Отвязать Telegram от этого сотрудника?')) return;
    setActionError(null);
    try {
      const { data } = await crmApi(branchId).delete(`/managers/${managerId}/telegram-account`);
      onUpdated(data.manager);
      setTelegramLink(null);
    } catch (e) {
      setActionError(errMsg(e, 'Не удалось отвязать Telegram'));
    }
  };

  const setSubscriptionEnabled = async (key, enabled) => {
    setSavingKey(key);
    setActionError(null);
    try {
      const { data } = await crmApi(branchId).patch(`/managers/${managerId}/notifications`, {
        subscriptions: [{ key, enabled }],
      });
      onUpdated(data.manager);
    } catch (e) {
      setActionError(errMsg(e, 'Не удалось сохранить настройку уведомления'));
    } finally {
      setSavingKey(null);
    }
  };

  const refreshManager = async () => {
    try {
      const { data } = await crmApi(branchId).get(`/managers/${managerId}`);
      onUpdated(data.manager);
    } catch (e) {
      setActionError(errMsg(e, 'Не удалось обновить данные'));
    }
  };

  const copyBotLink = async () => {
    if (!telegramLink?.botDeepLink) return;
    setCopyLinkState(null);
    try {
      await navigator.clipboard.writeText(telegramLink.botDeepLink);
      setCopyLinkState({ type: 'success', text: 'Ссылка скопирована' });
    } catch {
      setCopyLinkState({ type: 'error', text: 'Не удалось скопировать ссылку' });
    }
  };

  if (!manager || manager.isOwner || !isAdminRole(manager)) return null;

  return (
    <section className="clients-page__detail-block staff-detail-grid__notifications">
      <h3>Уведомления</h3>
      <p className="muted">
        Telegram-оповещения для администратора в текущем филиале
        {currentBranchName ? ` «${currentBranchName}»` : ''}.
        Сначала привяжите бота, затем включите нужные типы.
      </p>
      <div className="form-grid">
        <div style={{ gridColumn: '1 / -1' }}>
          {linked ? (
            <p>
              Telegram:{' '}
              {telegram.username ? `@${telegram.username}` : 'привязан'}
              {telegram.linkedAt ? ` (${formatTelegramLinkedAt(telegram.linkedAt)})` : ''}
            </p>
          ) : (
            <p className="muted">Telegram: не привязан</p>
          )}
          {canManage ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              <UiButton
                type="button"
                size="sm"
                disabled={telegramLinkLoading}
                onClick={createTelegramLink}
              >
                {telegramLinkLoading ? 'Создаём…' : linked ? 'Перепривязать' : 'Привязать Telegram'}
              </UiButton>
              {linked ? (
                <UiButton type="button" size="sm" variant="ghost" onClick={unlinkTelegram}>
                  Отвязать
                </UiButton>
              ) : null}
              <UiButton type="button" size="sm" variant="ghost" onClick={refreshManager}>
                Обновить
              </UiButton>
            </div>
          ) : null}
          {telegramLink ? (
            <div style={{ marginTop: 12, display: 'grid', gap: 8 }}>
              <div className="form-note">
                Код: <strong>{telegramLink.code}</strong>. Действует{' '}
                {Math.round(telegramLink.expiresIn / 60)} мин.
              </div>
              {telegramLink.botDeepLink ? (
                <>
                  <div className="copy-field">
                    <input
                      className="copy-field__input"
                      type="text"
                      value={telegramLink.botDeepLink}
                      readOnly
                    />
                    <UiButton type="button" variant="ghost" size="sm" onClick={copyBotLink}>
                      Скопировать
                    </UiButton>
                  </div>
                  {copyLinkState ? (
                    <div className={`form-note form-note--${copyLinkState.type}`}>
                      {copyLinkState.text}
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}
          {telegramLinkError ? (
            <div className="form-error" style={{ marginTop: 8 }}>{telegramLinkError}</div>
          ) : null}
        </div>
        {subscriptions.map((item) => (
          <UiCheckbox
            key={item.key}
            label={item.label}
            checked={Boolean(item.enabled)}
            disabled={!canManage || !item.available || savingKey === item.key}
            onChange={(e) => setSubscriptionEnabled(item.key, e.target.checked)}
          />
        ))}
        {!linked ? (
          <p className="muted" style={{ gridColumn: '1 / -1', margin: 0 }}>
            Сначала привяжите Telegram
          </p>
        ) : null}
        {actionError ? (
          <div className="form-error" style={{ gridColumn: '1 / -1' }}>{actionError}</div>
        ) : null}
      </div>
    </section>
  );
}

export function StaffDetailPage() {
  const { branchId, managerId } = useParams();
  const crmBranches = useSelector((s) => s.crmAuth.branches ?? []);
  const { profile } = useSelector((s) => s.crmAuth);
  const canManageAccess = canManageCrmAccess(profile);
  const canManageNotifications = hasCrmPermission(
    profile,
    crmBranches,
    branchId,
    'staff.notifications.manage',
  );
  const [manager, setManager] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [branchDraftId, setBranchDraftId] = useState('');
  const [branchError, setBranchError] = useState(null);
  const [branchSaving, setBranchSaving] = useState(false);
  const [achievementDraft, setAchievementDraft] = useState('');
  const [scheduleForm, setScheduleForm] = useState(emptyScheduleForm);
  const [calendarPeriod, setCalendarPeriod] = useState({
    from: todayString(),
    to: monthEndString(),
  });
  const [calendarFillStatus, setCalendarFillStatus] = useState('idle');
  const [calendarFillMessage, setCalendarFillMessage] = useState('');
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: {
      login: '',
      name: '',
      status: 1,
      role: 2,
      salaryType: '',
      salaryAmount: '',
      schedule: '',
      description: '',
      achievements: [],
      roleAdmin: false,
      roleCoach: false,
      permissionPreset: DEFAULT_ADMIN_PERMISSION_PRESET,
    },
  });
  const achievements = watch('achievements') ?? [];
  const roleAdmin = watch('roleAdmin');
  const roleCoach = watch('roleCoach');

  const branchSelectOptions = useMemo(() => {
    const used = new Set((manager?.branches ?? []).map((branch) => branch.id));
    return crmBranches
      .filter((branch) => !used.has(branch.id))
      .map((branch) => ({ value: branch.id, label: branch.name ?? branch.id }));
  }, [crmBranches, manager?.branches]);

  const loadManager = async () => {
    if (!branchId || !managerId) return;
    setStatus('loading');
    setError(null);
    try {
      const { data } = await crmApi(branchId).get(`/managers/${managerId}`);
      const item = data.manager;
      setManager(item);
      setScheduleForm(parseSchedule(item.profile?.schedule));
      reset({
        login: item.login ?? '',
        name: item.name ?? '',
        status: formStatus(item.status),
        role: item.role ?? 2,
        salaryType: adminSalaryType(item.salaryType),
        salaryAmount: item.salaryAmount ?? '',
        schedule: item.profile?.schedule ?? '',
        description: item.profile?.description ?? '',
        achievements: item.profile?.achievements ?? [],
        roleAdmin: isAdminRole(item),
        roleCoach: isCoachRole(item),
        permissionPreset: item.permissionPreset || DEFAULT_ADMIN_PERMISSION_PRESET,
      });
      setStatus('idle');
    } catch (e) {
      setError(errMsg(e, 'Не удалось загрузить сотрудника'));
      setStatus('error');
    }
  };

  useEffect(() => {
    loadManager();
  }, [branchId, managerId]);

  const admin = Boolean(roleAdmin || manager?.isOwner);
  const coach = Boolean(roleCoach);

  const addAchievement = () => {
    const value = achievementDraft.trim();
    if (!value) return;
    setValue('achievements', [...achievements, value], { shouldDirty: true });
    setAchievementDraft('');
  };

  const setRegularScheduleDay = (day, checked) => {
    setScheduleForm((prev) => {
      const regular = { ...(prev.regular ?? {}) };
      if (checked) {
        regular[day] = regular[day] ?? { timeStart: '10:00', timeEnd: '18:00' };
      } else {
        delete regular[day];
      }
      return { ...prev, regular };
    });
  };

  const setRegularScheduleTime = (day, key, value) => {
    setScheduleForm((prev) => ({
      ...prev,
      regular: {
        ...(prev.regular ?? {}),
        [day]: {
          ...(prev.regular?.[day] ?? { timeStart: '10:00', timeEnd: '18:00' }),
          [key]: value,
        },
      },
    }));
  };

  const fillCalendar = async () => {
    setCalendarFillStatus('loading');
    setCalendarFillMessage('');
    try {
      const { data } = await crmApi(branchId).post(`/managers/${managerId}/calendar`, {
        ...calendarPeriod,
        schedule: serializeSchedule(scheduleForm),
      });
      setCalendarFillMessage(`Календарь заполнен. Создано записей: ${data.created ?? 0}`);
      setCalendarFillStatus('idle');
    } catch (e) {
      setCalendarFillMessage(errMsg(e, 'Не удалось заполнить календарь'));
      setCalendarFillStatus('error');
    }
  };

  const onSubmit = async (values) => {
    setSaveError(null);
    if (!values.roleAdmin && !values.roleCoach && !manager?.isOwner) {
      setSaveError('Выберите хотя бы один тип сотрудника');
      return;
    }
    const payload = {
      name: values.name.trim() || null,
      status: Number(values.status),
      role: manager?.isOwner ? 99 : values.roleAdmin ? 2 : 3,
      roles: manager?.isOwner
        ? [99, 2]
        : [values.roleAdmin ? 2 : null, values.roleCoach ? 3 : null].filter(Boolean),
      salaryType: values.salaryType === '' ? null : Number(values.salaryType),
      salaryAmount: values.salaryAmount === '' ? null : Number(values.salaryAmount),
      schedule: admin ? serializeSchedule(scheduleForm) : null,
      description: coach ? values.description.trim() || null : null,
      achievements: coach ? (values.achievements ?? []) : [],
      login: values.login.trim() || null,
    };
    if (admin && canManageAccess) {
      const currentPreset = manager?.permissionPreset || DEFAULT_ADMIN_PERMISSION_PRESET;
      const nextPreset = values.permissionPreset || DEFAULT_ADMIN_PERMISSION_PRESET;
      if (nextPreset !== currentPreset) {
        payload.permissionPreset = nextPreset;
      }
    }
    try {
      const { data } = await crmApi(branchId).patch(`/managers/${managerId}`, payload);
      setManager(data.manager);
      setScheduleForm(parseSchedule(data.manager.profile?.schedule));
      reset({
        login: data.manager.login ?? '',
        name: data.manager.name ?? '',
        status: formStatus(data.manager.status),
        role: data.manager.role ?? 2,
        salaryType: adminSalaryType(data.manager.salaryType),
        salaryAmount: data.manager.salaryAmount ?? '',
        schedule: data.manager.profile?.schedule ?? '',
        description: data.manager.profile?.description ?? '',
        achievements: data.manager.profile?.achievements ?? [],
        roleAdmin: isAdminRole(data.manager),
        roleCoach: isCoachRole(data.manager),
        permissionPreset: data.manager.permissionPreset || DEFAULT_ADMIN_PERMISSION_PRESET,
      });
    } catch (e) {
      setSaveError(errMsg(e, 'Не удалось сохранить сотрудника'));
    }
  };

  const onAddBranch = async () => {
    if (!manager?.id || !branchDraftId) return;
    setBranchError(null);
    setBranchSaving(true);
    try {
      await crmApi(branchId).post(`/managers/${manager.id}/branches`, { branchId: branchDraftId });
      setBranchDraftId('');
      await loadManager();
    } catch (e) {
      setBranchError(errMsg(e, 'Не удалось добавить филиал'));
    } finally {
      setBranchSaving(false);
    }
  };

  const onRemoveBranch = async (targetBranchId) => {
    if (!manager?.id || !targetBranchId) return;
    if (String(targetBranchId) === String(branchId)) {
      setBranchError('Нельзя удалить привязку к текущему филиалу');
      return;
    }
    setBranchError(null);
    setBranchSaving(true);
    try {
      await crmApi(branchId).delete(`/managers/${manager.id}/branches/${targetBranchId}`);
      await loadManager();
    } catch (e) {
      setBranchError(errMsg(e, 'Не удалось удалить филиал'));
    } finally {
      setBranchSaving(false);
    }
  };

  return (
    <section className="clients-page clients-page--detail">
      <header className="page-header page-header--spread">
        <div>
          <Link to="../staffs" className="clients-page__back">
            ← Сотрудники
          </Link>
        </div>
      </header>

      <div className="form-card form-card--wide">
        {status === 'loading' ? <div className="placeholder">Загрузка…</div> : null}
        {error ? <div className="form-error">{error}</div> : null}

        {manager ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div
              className={
                `clients-page__detail-grid staff-detail-grid ${
                  admin && coach
                    ? 'staff-detail-grid--two-roles'
                    : admin
                      ? 'staff-detail-grid--admin-only'
                      : 'staff-detail-grid--coach-only'
                }`
              }
            >
              <div className="staff-detail-grid__main-column">
              <section className="clients-page__detail-block staff-detail-grid__main">
                <h3>Основная информация</h3>
                <div className="form-grid">
                  <UiInput
                    label="Ф.И.О."
                    className="field"
                    style={{ gridColumn: '1 / -1' }}
                    {...register('name')}
                  />
                  <UiInput
                    label={roleAdmin || manager.isOwner ? 'Email' : 'Email (необязательно)'}
                    type="email"
                    className="field"
                    style={{ gridColumn: '1 / -1' }}
                    {...register('login', {
                      required: roleAdmin || manager.isOwner
                        ? 'Email обязателен для администратора'
                        : false,
                    })}
                  />
                  <div className="staff-type-block" style={{ gridColumn: '1 / -1' }}>
                    <div className="staff-type-block__title">Тип сотрудника</div>
                    <p className="staff-type-block__hint">
                      Определяет, как сотрудник работает в системе: администратор студии или педагог.
                    </p>
                    <div className="staff-type-block__options">
                      <UiCheckbox
                        label="Администратор"
                        checked={Boolean(roleAdmin || manager.isOwner)}
                        disabled={manager.isOwner}
                        onChange={(e) => setValue('roleAdmin', e.target.checked, { shouldDirty: true })}
                      />
                      <UiCheckbox
                        label="Педагог"
                        checked={Boolean(roleCoach)}
                        disabled={manager.isOwner}
                        onChange={(e) => setValue('roleCoach', e.target.checked, { shouldDirty: true })}
                      />
                    </div>
                  </div>
                  <div className="staff-type-block__summary">
                    <div className="muted">Активные типы</div>
                    <strong>{roleLabel(manager)}</strong>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 10 }}>
                      <UiSelect
                        className="field"
                        style={{ flex: 1 }}
                        label="Привязать к другому филиалу"
                        value={branchDraftId}
                        onChange={setBranchDraftId}
                        options={branchSelectOptions}
                        placeholder="Выберите филиал"
                      />
                      <UiButton
                        type="button"
                        size="sm"
                        disabled={!branchDraftId || branchSaving}
                        onClick={onAddBranch}
                        aria-label="Добавить филиал"
                      >
                        <svg width="16" height="18" viewBox="0 0 14 14" fill="none" aria-hidden>
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7 0C6.58579 0 6.25 0.335786 6.25 0.75V6.25H0.75C0.335786 6.25 0 6.58579 0 7C0 7.41421 0.335786 7.75 0.75 7.75H6.25V13.25C6.25 13.6642 6.58579 14 7 14C7.41421 14 7.75 13.6642 7.75 13.25V7.75H13.25C13.6642 7.75 14 7.41421 14 7C14 6.58579 13.6642 6.25 13.25 6.25H7.75V0.75C7.75 0.335786 7.41421 0 7 0Z"
                            fill="currentColor"
                          />
                        </svg>
                      </UiButton>
                    </div>
                    {(manager?.branches ?? []).length ? (
                      <ul className="plain-list">
                        {manager.branches.map((branch) => {
                          const isCurrentBranch = String(branch.id) === String(branchId);
                          return (
                            <li key={branch.id} className="plain-list__item">
                              <span>
                                {branch.name ?? branch.id}
                                {isCurrentBranch ? ' (текущий)' : ''}
                              </span>
                              {!isCurrentBranch ? (
                                <UiButton
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  disabled={branchSaving}
                                  onClick={() => onRemoveBranch(branch.id)}
                                  aria-label={`Удалить филиал ${branch.name ?? branch.id}`}
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                </UiButton>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <div className="clients-page__detail-empty">Нет филиалов</div>
                    )}
                    <div className="clients-page__detail-empty" style={{ marginTop: 8 }}>
                      Удаление доступно только для привязок к другим филиалам, текущий филиал удалить здесь нельзя
                    </div>
                    {branchError ? <div className="form-error">{branchError}</div> : null}
                  </div>
                </div>
              </section>

              <StaffNotificationsSection
                branchId={branchId}
                managerId={managerId}
                manager={manager}
                canManage={canManageNotifications}
                onUpdated={setManager}
              />
              </div>

              {admin ? (
                <section className="clients-page__detail-block staff-detail-grid__admin">
                  <h3>Параметры администратора</h3>
                  <p className="staff-admin-block__intro muted">
                    Настройки для сотрудника с типом «Администратор»: доступ в CRM, ставка и расписание.
                  </p>
                  <div className="form-grid staff-detail-admin-form">
                    {canManageAccess ? (
                      <div className="staff-access-level-block" style={{ gridColumn: '1 / -1' }}>
                        <label className="ui-field">
                          <span className="ui-field__label">Уровень доступа</span>
                          <UiNativeSelect {...register('permissionPreset')}>
                            {ADMIN_PERMISSION_PRESETS.map((preset) => (
                              <option key={preset.key} value={preset.key}>
                                {preset.label}
                              </option>
                            ))}
                          </UiNativeSelect>
                        </label>
                        <p className="staff-access-level-block__hint muted">
                          Иерархия в студии и набор прав в CRM. Не путать с типом сотрудника слева.
                          Сейчас: {adminAccessLevelLabel(watch('permissionPreset'))}.
                        </p>
                      </div>
                    ) : null}
                    <label className="ui-field">
                      <span className="ui-field__label">Статус</span>
                      <UiNativeSelect {...register('status')}>
                        <option value={1}>Активен</option>
                        <option value={0}>Заблокирован</option>
                      </UiNativeSelect>
                    </label>
                    <label className="ui-field">
                      <span className="ui-field__label">Тип ставки</span>
                      <UiNativeSelect {...register('salaryType')}>
                        <option value="">—</option>
                        <option value={1}>Фиксированная</option>
                        <option value={2}>Почасовая</option>
                      </UiNativeSelect>
                    </label>
                    <UiInput label="Сумма ставки" type="number" {...register('salaryAmount')} />
                    <div className="ui-field" style={{ gridColumn: '1 / -1' }}>
                      <span className="ui-field__label">Расписание</span>
                      <UiNativeSelect
                        value={scheduleForm.type}
                        onChange={(e) => setScheduleForm((prev) => ({ ...prev, type: e.target.value }))}
                      >
                        <option value="once">Разовое</option>
                        <option value="regular">Регулярное</option>
                      </UiNativeSelect>
                    </div>

                    {scheduleForm.type === 'once' ? (
                      <>
                        <UiInput
                          label="Дата выхода"
                          type="date"
                          value={scheduleForm.once.date}
                          onChange={(e) => setScheduleForm((prev) => ({
                            ...prev,
                            once: { ...prev.once, date: e.target.value },
                          }))}
                        />
                        <UiTimePicker
                          label="С"
                          value={scheduleForm.once.timeStart}
                          onChange={(e) => setScheduleForm((prev) => ({
                            ...prev,
                            once: { ...prev.once, timeStart: e.target.value },
                          }))}
                        />
                        <UiTimePicker
                          label="До"
                          value={scheduleForm.once.timeEnd}
                          onChange={(e) => setScheduleForm((prev) => ({
                            ...prev,
                            once: { ...prev.once, timeEnd: e.target.value },
                          }))}
                        />
                      </>
                    ) : (
                      <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 10 }}>
                        {WEEK_DAYS.map((day) => {
                          const enabled = Boolean(scheduleForm.regular?.[day.value]);
                          return (
                            <div
                              key={day.value}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(150px, 1fr) minmax(110px, 150px) minmax(110px, 150px)',
                                gap: 8,
                                alignItems: 'center',
                              }}
                            >
                              <UiCheckbox
                                label={day.label}
                                checked={enabled}
                                onChange={(e) => setRegularScheduleDay(day.value, e.target.checked)}
                              />
                              <UiTimePicker
                                aria-label={`${day.label}: начало`}
                                disabled={!enabled}
                                value={scheduleForm.regular?.[day.value]?.timeStart ?? '10:00'}
                                onChange={(e) => setRegularScheduleTime(day.value, 'timeStart', e.target.value)}
                              />
                              <UiTimePicker
                                aria-label={`${day.label}: окончание`}
                                disabled={!enabled}
                                value={scheduleForm.regular?.[day.value]?.timeEnd ?? '18:00'}
                                onChange={(e) => setRegularScheduleTime(day.value, 'timeEnd', e.target.value)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
                      <div className="ui-field__label" style={{ marginBottom: 8 }}>Заполнить календарь</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                        <UiInput
                          label="С"
                          type="date"
                          value={calendarPeriod.from}
                          onChange={(e) => setCalendarPeriod((prev) => ({ ...prev, from: e.target.value }))}
                        />
                        <UiInput
                          label="До"
                          type="date"
                          value={calendarPeriod.to}
                          onChange={(e) => setCalendarPeriod((prev) => ({ ...prev, to: e.target.value }))}
                        />
                        <UiButton
                          type="button"
                          onClick={fillCalendar}
                          disabled={calendarFillStatus === 'loading'}
                        >
                          {calendarFillStatus === 'loading' ? 'Заполнение…' : 'Заполнить'}
                        </UiButton>
                      </div>
                      {calendarFillMessage ? (
                        <div
                          className={calendarFillStatus === 'error' ? 'form-error' : 'clients-page__detail-empty'}
                          style={{ marginTop: 8 }}
                        >
                          {calendarFillMessage}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </section>
              ) : null}

              {coach ? (
                <section className="clients-page__detail-block staff-detail-grid__coach">
                  <h3>Педагог</h3>
                  <div className="ui-field">
                    <span className="ui-field__label">Описание</span>
                    <UiTextarea rows={6} {...register('description')} />
                  </div>
                </section>
              ) : null}

              {coach ? (
                <section className="clients-page__detail-block staff-detail-grid__directions">
                  <h3>Направления педагога</h3>
                  {manager.coachDirections?.length ? (
                    <ul className="plain-list">
                      {manager.coachDirections.map((direction) => (
                        <li key={direction.id} className="plain-list__item">
                          <Link to={`../directions/${direction.id}`}>{direction.name}</Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="clients-page__detail-empty">Педагог пока не привязан к направлениям</div>
                  )}
                </section>
              ) : null}

              {coach ? (
                <section className="clients-page__detail-block staff-detail-grid__achievements">
                  <h3>Достижения</h3>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12, width: '100%' }}>
                    <UiInput
                      label="Добавить достижение"
                      style={{ flex: 1 }}
                      value={achievementDraft}
                      onChange={(e) => setAchievementDraft(e.target.value)}
                      placeholder="Введите достижение"
                    />
                    <UiButton type="button" onClick={addAchievement}>
                      Добавить
                    </UiButton>
                  </div>
                  {achievements.length ? (
                    <ul className="plain-list">
                      {achievements.map((item, idx) => (
                        <li key={`${item}-${idx}`} className="plain-list__item">
                          <span>{item}</span>
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => setValue(
                              'achievements',
                              achievements.filter((_, i) => i !== idx),
                              { shouldDirty: true },
                            )}
                          >
                            удалить
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="clients-page__detail-empty">Достижений пока нет</div>
                  )}
                </section>
              ) : null}

              <section className="clients-page__detail-save">
                {saveError ? <div className="form-error">{saveError}</div> : null}
                <UiButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Сохранение…' : 'Сохранить'}
                </UiButton>
              </section>
            </div>
          </form>
        ) : null}
      </div>
    </section>
  );
}

function StaffCreateModal({ open, branchId, onClose, onCreated, saveStatus, saveError }) {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      login: '',
      name: '',
      password: '',
      roleAdmin: true,
      roleCoach: false,
    },
  });
  const roleAdmin = watch('roleAdmin');
  const roleCoach = watch('roleCoach');

  useEffect(() => {
    if (!open) return;
    reset({
      login: '',
      name: '',
      password: '',
      roleAdmin: true,
      roleCoach: false,
    });
  }, [open, reset]);

  const onSubmit = async (values) => {
    if (!values.roleAdmin && !values.roleCoach) {
      setValue('roleAdmin', false, { shouldValidate: true });
      setValue('roleCoach', false, { shouldValidate: true });
      return;
    }

    const roles = [values.roleAdmin ? 2 : null, values.roleCoach ? 3 : null].filter(Boolean);
    const login = values.login.trim();
    const password = values.password.trim();
    const payload = {
      login: login || null,
      name: values.name.trim() || null,
      password: password || null,
      status: 1,
      role: values.roleAdmin ? 2 : 3,
      roles,
    };
    const res = await dispatch(managersThunks.create({ branchId, payload }));
    if (res.meta.requestStatus === 'fulfilled') onCreated(res.payload?.item ?? null);
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-staff-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-card__header">
          <h2 id="new-staff-title">Новый сотрудник</h2>
          <UiButton variant="ghost" size="sm" type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </UiButton>
        </header>
        <form onSubmit={handleSubmit(onSubmit)} className="modal-card__body">
          <h3 style={{ margin: 0 }}>Основная информация</h3>
          <div className="form-grid">
            <UiInput
              label="Ф.И.О."
              style={{ gridColumn: '1 / -1' }}
              autoFocus
              {...register('name')}
            />
            <UiInput
              label={roleAdmin ? 'Email / логин' : 'Email / логин (необязательно)'}
              type="email"
              style={{ gridColumn: '1 / -1' }}
              {...register('login', {
                validate: (value, formValues) => {
                  if (formValues.roleAdmin && !String(value ?? '').trim()) {
                    return 'Обязательно для администратора';
                  }
                  return true;
                },
              })}
            />
            {errors.login ? (
              <div className="form-error" style={{ gridColumn: '1 / -1' }}>
                {errors.login.message}
              </div>
            ) : null}
            <UiInput
              label={roleAdmin ? 'Пароль' : 'Пароль (необязательно)'}
              type="password"
              style={{ gridColumn: '1 / -1' }}
              {...register('password', {
                validate: (value, formValues) => {
                  const password = String(value ?? '');
                  const login = String(formValues.login ?? '').trim();
                  if (formValues.roleAdmin && !password.trim()) {
                    return 'Обязательно для администратора';
                  }
                  if (password && password.length < 4) {
                    return 'Минимум 4 символа';
                  }
                  if (login && !password.trim() && !formValues.roleAdmin) {
                    return 'Укажите пароль или оставьте email пустым';
                  }
                  return true;
                },
              })}
            />
            {errors.password ? (
              <div className="form-error" style={{ gridColumn: '1 / -1' }}>
                {errors.password.message}
              </div>
            ) : null}
            <div className="staff-type-block" style={{ gridColumn: '1 / -1' }}>
              <div className="staff-type-block__title">Тип сотрудника</div>
              <p className="staff-type-block__hint">
                Администратор — работа студии. Педагог — ведение занятий. Можно выбрать оба типа.
              </p>
              <div className="staff-type-block__options">
                <UiCheckbox
                  label="Администратор"
                  checked={Boolean(roleAdmin)}
                  onChange={(e) => {
                    setValue('roleAdmin', e.target.checked, { shouldDirty: true });
                    void trigger(['login', 'password']);
                  }}
                />
                <UiCheckbox
                  label="Педагог"
                  checked={Boolean(roleCoach)}
                  onChange={(e) => {
                    setValue('roleCoach', e.target.checked, { shouldDirty: true });
                    void trigger(['login', 'password']);
                  }}
                />
              </div>
              {!roleAdmin && !roleCoach ? (
                <div className="form-error" style={{ marginTop: 8 }}>
                  Выберите хотя бы один тип
                </div>
              ) : null}
            </div>
          </div>

          {saveError ? <div className="form-error">{saveError}</div> : null}

          <div className="modal-card__actions">
            <UiButton type="submit" disabled={saveStatus === 'loading'}>
              {saveStatus === 'loading' ? 'Создание…' : 'Создать'}
            </UiButton>
            <UiButton variant="ghost" type="button" onClick={onClose}>
              Отменить
            </UiButton>
          </div>
        </form>
      </div>
    </div>
  );
}
